import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_POSTER = "https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/5vkuqdp0_ChatGPT%20Image%20May%2022%2C%202026%2C%2001_51_33%20PM.png";

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, 80]);
  const opacityBg = useTransform(scrollY, [0, 500], [1, 0.55]);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-ink-900"
    >
      {/* Brand poster full-bleed */}
      <motion.div style={{ y: yBg, opacity: opacityBg }} className="absolute inset-0">
        <img
          src={HERO_POSTER}
          alt="CASH OUT NEVER CRASH OUT — Secure the bag. Stay in control. Build your future."
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        {/* Bottom fade so CTAs sit cleanly */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink-900 via-ink-900/80 to-transparent" />
        {/* Side vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.55)_100%)]" />
      </motion.div>

      {/* Grain */}
      <div className="absolute inset-0 bg-grain opacity-25 mix-blend-overlay pointer-events-none" />

      {/* Top eyebrow */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="absolute left-1/2 top-28 z-10 -translate-x-1/2 px-6 text-center md:top-32"
      >
        <div className="inline-flex items-center gap-3 rounded-full border border-gold/30 bg-black/40 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-gold backdrop-blur md:text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
          A Movement by Damien Doran — Mr. Green Bay
        </div>
      </motion.div>

      {/* Bottom content overlay */}
      <div className="relative z-10 mx-auto flex min-h-[100vh] max-w-7xl flex-col justify-end px-6 pb-28 pt-40 md:px-12 md:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="max-w-2xl"
        >
          <p className="font-display text-2xl italic leading-snug text-gold-pale md:text-3xl">
            Leadership. Loyalty. Legacy.
          </p>
          <p className="mt-4 text-base text-zinc-300 md:text-lg">
            A movement helping youth choose <span className="text-gold-pale">purpose</span> over destruction —
            and turn pain into blueprint.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="hero-cta-book-speaker"
              onClick={() => scrollTo("#speaking")}
              className="btn-gold rounded-full px-6 py-3.5 text-xs font-semibold uppercase tracking-wider md:text-sm"
            >
              <i className="fa-solid fa-microphone-lines mr-2" />
              Book Damien To Speak
            </button>
            <button
              data-testid="hero-cta-get-book"
              onClick={() => window.open("https://a.co/d/0inHxgPY", "_blank", "noopener,noreferrer")}
              className="btn-ghost-gold rounded-full px-6 py-3.5 text-xs font-semibold uppercase tracking-wider md:text-sm"
            >
              <i className="fa-brands fa-amazon mr-2" />
              Get The Book
            </button>
            <button
              data-testid="hero-cta-join-movement"
              onClick={() => scrollTo("#sponsor")}
              className="btn-ghost-gold rounded-full px-6 py-3.5 text-xs font-semibold uppercase tracking-wider md:text-sm"
            >
              <i className="fa-solid fa-fire mr-2" />
              Sponsor A Youth
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 md:text-xs"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            Currently touring schools, summits & reentry programs nationwide
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/5 bg-black/70 py-4 backdrop-blur">
        <div className="scroll-fade-mask overflow-hidden">
          <div className="marquee-track flex w-max gap-12 whitespace-nowrap text-sm uppercase tracking-[0.3em] text-zinc-400">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={`marquee-${dup}`} className="flex gap-12">
                {[
                  "Leadership.",
                  "Loyalty.",
                  "Legacy.",
                  "Crash Proof.",
                  "Mirror Test Mentality.",
                  "Pain Into Purpose.",
                  "Self-Respect Is The Real Flex.",
                  "Legacy Over Likes."
                ].map((w, i) => (
                  <span key={`${dup}-${i}`} className="flex items-center gap-12">
                    {w}
                    <i className="fa-solid fa-diamond text-[8px] text-gold" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
